import { toast } from 'vue-sonner';

import { i18n } from '@/i18n';
import { useAppStore } from '@stores/app.store';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) {
    return '';
  }

  const entries = Object.entries(params).filter(
    ([, paramValue]) => paramValue !== undefined && paramValue !== null && paramValue !== '',
  );

  if (entries.length === 0) {
    return '';
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of entries) {
    searchParams.set(key, String(value));
  }

  return `?${searchParams.toString()}`;
}

function getLocaleHeader(): string {
  try {
    const store = useAppStore();

    return store.locale;
  } catch {
    return 'uz';
  }
}

function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('auth');

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as { accessToken?: string };

    return parsed.accessToken ?? null;
  } catch {
    return null;
  }
}

function buildHeaders(contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  headers['X-Locale'] = getLocaleHeader();

  const token = getAuthToken();

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

function translate(key: string): string {
  return i18n.global.t(key);
}

function getStatusMessage(statusCode: number): string | null {
  const statusMessages: Record<number, string> = {
    400: 'errors.badRequest',
    401: 'errors.unauthorized',
    403: 'errors.accessDenied',
    404: 'errors.notFound',
    500: 'errors.serverError',
  };

  const translationKey = statusMessages[statusCode];

  return translationKey ? translate(translationKey) : null;
}

function showErrorToast(apiMessage: string, statusCode: number): void {
  const localizedMessage = getStatusMessage(statusCode);

  toast.error(localizedMessage ?? apiMessage);
}

let refreshPromise: Promise<void> | null = null;

async function handleUnauthorized(): Promise<void> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { useAuthStore } = await import('@/stores/auth.store');
      const authStore = useAuthStore();

      await authStore.refresh();
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function request<T>(
  method: string,
  path: string,
  options?: { body?: unknown; params?: Record<string, unknown>; skipRefresh?: boolean },
): Promise<T> {
  const url = `/api${path}${buildQueryString(options?.params)}`;
  const headers = buildHeaders('application/json');

  const response = await fetch(url, {
    method,
    headers,
    body: options?.body != null ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401 && !options?.skipRefresh) {
    try {
      await handleUnauthorized();

      return await request<T>(method, path, { ...options, skipRefresh: true });
    } catch {
      const body: unknown = await response.json().catch(() => null);

      showErrorToast('Unauthorized', 401);

      throw new ApiClientError('Unauthorized', 401, body);
    }
  }

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const bodyObj = body as Record<string, unknown> | null;
    const message =
      typeof bodyObj?.['message'] === 'string' ? bodyObj['message'] : response.statusText;

    showErrorToast(message, response.status);

    throw new ApiClientError(message, response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    return request<T>('GET', path, { params });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('POST', path, { body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PUT', path, { body });
  },

  delete(path: string): Promise<void> {
    return request<undefined>('DELETE', path) as Promise<void>;
  },

  async upload<T>(path: string, formData: FormData): Promise<T> {
    const url = `/api${path}`;
    const headers = buildHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      try {
        await handleUnauthorized();

        return await this.upload<T>(path, formData);
      } catch {
        const body: unknown = await response.json().catch(() => null);

        showErrorToast('Unauthorized', 401);

        throw new ApiClientError('Unauthorized', 401, body);
      }
    }

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const bodyObj = body as Record<string, unknown> | null;
      const message =
        typeof bodyObj?.['message'] === 'string' ? bodyObj['message'] : response.statusText;

      showErrorToast(message, response.status);

      throw new ApiClientError(message, response.status, body);
    }

    return response.json() as Promise<T>;
  },

  async downloadBlob(path: string): Promise<Blob> {
    const url = `/api${path}`;
    const headers = buildHeaders();

    const response = await fetch(url, { method: 'GET', headers });

    if (response.status === 401) {
      try {
        await handleUnauthorized();

        return await this.downloadBlob(path);
      } catch {
        const body: unknown = await response.json().catch(() => null);

        showErrorToast('Unauthorized', 401);

        throw new ApiClientError('Unauthorized', 401, body);
      }
    }

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const bodyObj = body as Record<string, unknown> | null;
      const message =
        typeof bodyObj?.['message'] === 'string' ? bodyObj['message'] : response.statusText;

      showErrorToast(message, response.status);

      throw new ApiClientError(message, response.status, body);
    }

    return response.blob();
  },
};
